import React, { useState } from "react";
import { Sparkles, Check } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";

const EXAMPLE_NOTES = `Meeting with Anne Hansen (NRK) and Per Olsen, 45 min, Teams.

Anne confirmed the studio will be used for both the regional news window at 18:45 and recorded digital content during the day. They want to be able to run it with one operator, sometimes none. The IT department insists that the media network is completely separate from the office network and that Mediability documents all firewall openings.

Per said procurement can approve up to 2.5 MNOK without a tender process, so we must stay below that. They would like an option for a second control position.

Decided: we go for four cameras. Anne will send the floor plan by Friday. Next step is a technical workshop with NRK IT on the 3rd. Erik should join.

Open: redundancy level, whether they keep the old Ross switcher as backup, and SLA expectations.`;

export function MeetingDialog({ open, onOpenChange, opportunityId, onDone }) {
  const [notes, setNotes] = useState("");
  const [title, setTitle] = useState("Customer meeting");
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState({});

  const summarise = async () => {
    if (notes.trim().length < 20) return toast.error("Paste the meeting notes or transcript first");
    setBusy(true);
    try {
      const r = await api.post(`/opportunities/${opportunityId}/ai/meeting`, { notes, title });
      setResult(r.data);
      const sel = {};
      (r.data.new_requirements || []).forEach((_, i) => { sel[i] = true; });
      setSelected(sel);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  const apply = async () => {
    setApplying(true);
    try {
      const body = { ...result, new_requirements: (result.new_requirements || []).filter((_, i) => selected[i]) };
      const r = await api.post(`/opportunities/${opportunityId}/ai/meeting/apply`, body);
      toast.success(`Opportunity updated: ${r.data.added_requirements} requirements, ${r.data.tasks} tasks`);
      onDone?.();
      close(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setApplying(false);
    }
  };

  const close = (v) => {
    if (!v) { setResult(null); setNotes(""); }
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[680px]" data-testid="meeting-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl">Summarise meeting</DialogTitle>
          <DialogDescription>Paste notes or a Teams transcript. The AI produces summary, decisions, new requirements, open questions, tasks and next step.</DialogDescription>
        </DialogHeader>
        {!result ? (
          <div className="space-y-3">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="h-9 rounded-[10px]" placeholder="Meeting title" data-testid="meeting-title-input" />
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={10} className="rounded-xl text-sm leading-6" placeholder="Paste transcript or notes..." data-testid="meeting-notes-textarea" />
            <div className="flex items-center justify-between">
              <Button variant="ghost" onClick={() => setNotes(EXAMPLE_NOTES)} data-testid="meeting-use-example-button">Use example notes</Button>
              <Button onClick={summarise} disabled={busy} className="gap-2 bg-primary text-white hover:bg-[#ff8e76]" data-testid="meeting-summarise-button"><Sparkles className={`h-4 w-4 ${busy ? "animate-pulse" : ""}`} /> {busy ? "Summarising..." : "Summarise with AI"}</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-5 fade-in" data-testid="meeting-result">
            <Section title="Meeting summary"><p className="text-sm leading-6">{result.summary}</p></Section>
            {result.decisions?.length > 0 && <Section title="Decisions"><ul className="list-disc space-y-1 pl-5 text-sm">{result.decisions.map((d, i) => <li key={i}>{d}</li>)}</ul></Section>}
            {result.new_requirements?.length > 0 && (
              <Section title={`New requirements (${result.new_requirements.length})`}>
                <div className="space-y-2">
                  {result.new_requirements.map((r, i) => (
                    <label key={i} className="flex items-start gap-2 text-sm" data-testid="meeting-new-requirement">
                      <Checkbox checked={!!selected[i]} onCheckedChange={(v) => setSelected((s) => ({ ...s, [i]: !!v }))} className="mt-0.5" />
                      <span><span className="font-medium">{r.title}</span> <span className="text-muted-foreground">– {r.detail}</span> <span className="ml-1 rounded-full border px-1.5 text-[10px] text-muted-foreground">{r.category}</span></span>
                    </label>
                  ))}
                </div>
              </Section>
            )}
            {result.changed_requirements?.length > 0 && <Section title="Changed requirements"><ul className="space-y-1 text-sm">{result.changed_requirements.map((c, i) => <li key={i}><span className="font-medium">{c.title}</span>: {c.change}</li>)}</ul></Section>}
            {result.open_questions?.length > 0 && <Section title="Open questions"><ul className="list-disc space-y-1 pl-5 text-sm">{result.open_questions.map((q, i) => <li key={i}>{q}</li>)}</ul></Section>}
            {result.tasks?.length > 0 && <Section title="Tasks"><ul className="space-y-1 text-sm">{result.tasks.map((t, i) => <li key={i} className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-muted-foreground" />{t.title} <span className="text-xs text-muted-foreground">({t.owner}{t.due ? `, ${t.due}` : ""})</span></li>)}</ul></Section>}
            <Section title="Next step"><p className="text-sm font-medium">{result.next_step}</p></Section>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setResult(null)}>Back</Button>
              <Button onClick={apply} disabled={applying} className="gap-2 bg-primary text-white hover:bg-[#ff8e76]" data-testid="meeting-apply-button"><Check className="h-4 w-4" /> {applying ? "Updating..." : "Update opportunity"}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</div>
      {children}
    </div>
  );
}
